import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Download, Copy, GraduationCap, Clock, CheckCircle, AlertCircle, Loader, Eye, EyeOff, ExternalLink } from 'lucide-react'
import AgentHeader from '../../components/AgentHeader'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { formatProgress, estimateTimeRemaining, parseMarkdownContent, formatSourceToAPA, countWords } from './makalahUtils'
import { CheckCircle2 } from 'lucide-react'

// Component for displaying sources as cards
const SourceCard = ({ source, index }) => {
  // Extract domain from URL for favicon
  const getDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname
      return domain.replace('www.', '')
    } catch {
      return 'unknown'
    }
  }

  const domain = getDomainFromUrl(source.url)
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`

  return (
    <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary hover:border-border-primary transition-colors min-h-[120px] sm:h-32 flex flex-col">
      <div className="flex items-start space-x-2 sm:space-x-3 mb-2 sm:mb-3">
        <div className="flex-shrink-0">
          <img 
            src={faviconUrl}
            alt={`${domain} favicon`}
            className="w-4 h-4 sm:w-5 sm:h-5 rounded"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-violet-500/10 rounded flex items-center justify-center" style={{display: 'none'}}>
            <GraduationCap className="w-3 h-3 text-violet-500" />
          </div>
        </div>
        <div className="text-xs font-medium text-text-tertiary">
          Sumber {index + 1}
        </div>
      </div>
      
      <div className="flex-1 mb-2 sm:mb-3">
        <h4 className="text-sm font-semibold text-text-primary leading-tight line-clamp-2">
          {source.title}
        </h4>
      </div>
      
      <div className="mt-auto">
        <a 
          href={source.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors truncate w-full"
        >
          <ExternalLink className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">Buka Sumber</span>
        </a>
      </div>
    </div>
  )
}

// Component for rendering parsed markdown content without citations
const MarkdownContent = ({ content }) => {
  const { parsedContent } = parseMarkdownContent(content) || { parsedContent: '' }
  
  // Clean content to remove references and bibliographies that might appear at the end of chapters
  const cleanContent = parsedContent
    // Remove various reference section patterns
    .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
    .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
    // Remove numbered reference lists at the end
    .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
    // Remove author-year citation patterns
    .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
    // Remove bracketed citations
    .replace(/\[\d+\]/g, '')
    .trim()
  
  const wordCount = countWords(cleanContent)
  
  return (
    <div className="space-y-6">
      {/* Main Content */}
      <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({node, ...props}) => <h1 className="text-xl sm:text-2xl font-bold text-text-primary mb-3 sm:mb-4 mt-4 sm:mt-6" {...props} />,
            h2: ({node, ...props}) => <h2 className="text-lg sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3 mt-4 sm:mt-5" {...props} />,
            h3: ({node, ...props}) => <h3 className="text-base sm:text-lg font-medium text-text-primary mb-2 mt-3 sm:mt-4" {...props} />,
            h4: ({node, ...props}) => <h4 className="text-sm sm:text-base font-medium text-text-primary mb-2 mt-2 sm:mt-3" {...props} />,
            p: ({node, ...props}) => <p className="mb-3 sm:mb-4 text-text-secondary leading-relaxed text-sm sm:text-base" {...props} />,
            ul: ({node, ...props}) => <ul className="list-disc ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-sm sm:text-base" {...props} />,
            ol: ({node, ...props}) => <ol className="list-decimal ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-sm sm:text-base" {...props} />,
            li: ({node, ...props}) => <li className="mb-1 leading-relaxed" {...props} />,
            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-500 pl-3 sm:pl-4 italic text-text-tertiary mb-3 sm:mb-4 text-sm sm:text-base" {...props} />,
            code: ({node, inline, ...props}) => 
              inline 
                ? <code className="bg-bg-tertiary text-blue-400 px-1 py-0.5 rounded text-xs sm:text-sm" {...props} />
                : <code className="block bg-bg-tertiary text-text-secondary p-2 sm:p-4 rounded-lg mb-3 sm:mb-4 overflow-x-auto text-xs sm:text-sm" {...props} />,
            strong: ({node, ...props}) => <strong className="font-semibold text-text-primary" {...props} />,
            em: ({node, ...props}) => <em className="italic text-text-secondary" {...props} />,
            a: ({node, ...props}) => <a className="text-blue-400 hover:text-blue-300 underline break-words" {...props} />,
            table: ({node, ...props}) => (
              <div className="overflow-x-auto mb-4 sm:mb-6">
                <table className="w-full border-collapse border border-border-secondary rounded-lg text-xs sm:text-sm" {...props} />
              </div>
            ),
            th: ({node, ...props}) => <th className="border border-border-secondary bg-bg-tertiary p-2 sm:p-3 text-left font-semibold text-text-primary" {...props} />,
            td: ({node, ...props}) => <td className="border border-border-secondary p-2 sm:p-3 text-text-secondary" {...props} />
          }}
        >
          {cleanContent}
        </ReactMarkdown>
      </div>
    </div>
  )
}

const MakalahResultPage = ({ 
  query, 
  outline, 
  generatedContent, 
  academicSources = [], 
  progress, 
  isLoading, 
  error, 
  isCompleted, 
  onBack, 
  onBackToSearch 
}) => {
  const [startTime] = useState(Date.now())
  const [copyStatus, setCopyStatus] = useState('idle')
  const [expandedSections, setExpandedSections] = useState(new Set())
  const [showAllContent, setShowAllContent] = useState(false)

  // Auto scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const progressPercentage = formatProgress(progress.current, progress.total)
  const timeRemaining = estimateTimeRemaining(progress.current, progress.total, startTime)
  
  // Calculate word statistics
  const calculateWordStats = () => {
    let totalWords = 0
    const sectionStats = []
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      const wordCount = content ? countWords(content) : 0
      
      totalWords += wordCount
      
      if (wordCount > 0) {
        sectionStats.push({
          title: section.title,
          current: wordCount
        })
      }
    })
    
    return {
      totalWords,
      sectionStats
    }
  }
  
  const wordStats = calculateWordStats()

  // Collect all citations from all sections
  const collectAllCitations = () => {
    const allCitations = []
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content) {
        const { citations } = parseMarkdownContent(content) || { citations: [] }
        allCitations.push(...citations)
      }
    })
    return [...new Set(allCitations)].sort((a, b) => a - b)
  }

  const allCitations = collectAllCitations()

  // Generate full makalah content with proper rich text formatting for copy
  const generateFullContent = () => {
    // Create a temporary div to render rich text content
    const tempDiv = document.createElement('div')
    tempDiv.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roagento, sans-serif;
      line-height: 1.6;
      color: #000;
      background: #fff;
      padding: 20px;
      max-width: none;
    `
    
    // Add main title
    const mainTitle = document.createElement('h1')
    mainTitle.style.cssText = 'font-size: 28px; font-weight: bold; margin: 0 0 24px 0; color: #000; text-align: center;'
    mainTitle.textContent = query
    tempDiv.appendChild(mainTitle)
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content) {
        // Parse content to remove citations
        const { parsedContent } = parseMarkdownContent(content) || { parsedContent: content }
        
        // Clean content to remove references and bibliographies
        const cleanedContent = parsedContent
          .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
          .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
          .replace(/\[\d+\]/g, '')
          .trim()
        
        // Add section title as H2
        const sectionTitle = document.createElement('h2')
        sectionTitle.style.cssText = 'font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: #000;'
        sectionTitle.textContent = section.title
        tempDiv.appendChild(sectionTitle)
        
        // Process content to handle tables and other markdown elements
        let processedContent = cleanedContent
        
        // Remove table separator lines (|---|---|) first
        processedContent = processedContent.replace(/^\s*\|[-\s:]+\|\s*$/gm, '')
        
        // Remove empty lines that might be left after removing separators
        processedContent = processedContent.replace(/\n\s*\n/g, '\n')
        
        // Create a temporary container for markdown content
        const contentDiv = document.createElement('div')
        
        // Split content by lines to process each line
        const lines = processedContent.split('\n')
        let htmlContent = ''
        let inTable = false
        let tableRows = []
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim()
          
          // Check if this is a table row (but not a separator line)
          if (line.includes('|') && line.split('|').length > 2 && !line.match(/^\s*\|[-\s:]+\|\s*$/)) {
            if (!inTable) {
              inTable = true
              tableRows = []
            }
            const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell)
            // Only add rows that have actual content (not just separators)
            if (cells.length > 0 && !cells.every(cell => cell.match(/^[-\s:]*$/))) {
              tableRows.push(cells)
            }
          } else {
            // If we were in a table and now we're not, process the table
            if (inTable && tableRows.length > 0) {
              let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd;">'
              tableRows.forEach((row, rowIndex) => {
                const isHeader = rowIndex === 0
                const tag = isHeader ? 'th' : 'td'
                const style = isHeader 
                  ? 'border: 1px solid #ddd; padding: 8px 12px; background: #f5f5f5; font-weight: 600; text-align: left;'
                  : 'border: 1px solid #ddd; padding: 8px 12px;'
                
                tableHtml += '<tr>'
                row.forEach(cell => {
                  tableHtml += `<${tag} style="${style}">${cell}</${tag}>`
                })
                tableHtml += '</tr>'
              })
              tableHtml += '</table>'
              htmlContent += tableHtml
              inTable = false
              tableRows = []
            }
            
            // Process non-table content
            if (line) {
              // Skip heading markers that would duplicate section titles
              if (line.startsWith('##')) {
                continue
              }
              
              let processedLine = line
              
              // Convert markdown to HTML
              if (line.startsWith('### ')) {
                processedLine = `<h3 style="font-size: 18px; font-weight: 500; margin: 24px 0 12px 0; color: #000;">${line.substring(4)}</h3>`
              } else if (line.startsWith('#### ')) {
                processedLine = `<h4 style="font-size: 16px; font-weight: 500; margin: 20px 0 10px 0; color: #000;">${line.substring(5)}</h4>`
              } else if (line.startsWith('> ')) {
                processedLine = `<blockquote style="border-left: 4px solid #ddd; padding-left: 16px; margin: 16px 0; font-style: italic; color: #666;">${line.substring(2)}</blockquote>`
              } else if (line.match(/^[-*+]\s+/)) {
                // Check if previous line was also a list item to continue the list
                const prevLine = i > 0 ? lines[i-1].trim() : ''
                const nextLine = i < lines.length - 1 ? lines[i+1].trim() : ''
                const isPrevList = prevLine.match(/^[-*+]\s+/)
                const isNextList = nextLine.match(/^[-*+]\s+/)
                
                let listItem = `<li style="margin: 4px 0; padding-left: 8px;">${line.replace(/^[-*+]\s+/, '')}</li>`
                
                if (!isPrevList) {
                  listItem = `<ul style="margin: 16px 0; padding-left: 20px; list-style-type: disc;">` + listItem
                }
                if (!isNextList) {
                  listItem = listItem + `</ul>`
                }
                
                processedLine = listItem
              } else if (line.match(/^\d+\.\s+/)) {
                // Check if previous line was also a numbered list item
                const prevLine = i > 0 ? lines[i-1].trim() : ''
                const nextLine = i < lines.length - 1 ? lines[i+1].trim() : ''
                const isPrevList = prevLine.match(/^\d+\.\s+/)
                const isNextList = nextLine.match(/^\d+\.\s+/)
                
                let listItem = `<li style="margin: 4px 0; padding-left: 8px;">${line.replace(/^\d+\.\s+/, '')}</li>`
                
                if (!isPrevList) {
                  listItem = `<ol style="margin: 16px 0; padding-left: 20px; list-style-type: decimal;">` + listItem
                }
                if (!isNextList) {
                  listItem = listItem + `</ol>`
                }
                
                processedLine = listItem
              } else {
                // Regular paragraph
                processedLine = `<p style="margin: 16px 0; line-height: 1.6;">${line}</p>`
              }
              
              // Apply inline formatting
              processedLine = processedLine
                .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
                .replace(/`([^`]+)`/g, '<code style="background: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-family: \'Monaco\', \'Consolas\', monospace; font-size: 14px;">$1</code>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #0066cc; text-decoration: underline;">$1</a>')
              
              htmlContent += processedLine
            } else {
              htmlContent += '<br>'
            }
          }
        }
        
        // Handle any remaining table at the end
        if (inTable && tableRows.length > 0) {
          let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd;">'
          tableRows.forEach((row, rowIndex) => {
            const isHeader = rowIndex === 0
            const tag = isHeader ? 'th' : 'td'
            const style = isHeader 
              ? 'border: 1px solid #ddd; padding: 8px 12px; background: #f5f5f5; font-weight: 600; text-align: left;'
              : 'border: 1px solid #ddd; padding: 8px 12px;'
            
            tableHtml += '<tr>'
            row.forEach(cell => {
              tableHtml += `<${tag} style="${style}">${cell}</${tag}>`
            })
            tableHtml += '</tr>'
          })
          tableHtml += '</table>'
          htmlContent += tableHtml
        }
        
        // Handle code blocks
        htmlContent = htmlContent.replace(/```[\s\S]*?```/g, (match) => {
          const code = match.replace(/```\w*\n?/g, '').replace(/```$/g, '')
          return `<pre style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin: 16px 0; font-family: 'Monaco', 'Consolas', monospace; font-size: 14px; overflow-x: auto; white-space: pre-wrap;">${code}</pre>`
        })
        
        contentDiv.innerHTML = htmlContent
        tempDiv.appendChild(contentDiv)
      }
    })
    
    // Append to body temporarily to copy rich text
    document.body.appendChild(tempDiv)
    
    // Select and copy the content
    const range = document.createRange()
    range.selectNodeContents(tempDiv)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    
    let success = false
    try {
      success = document.execCommand('copy')
    } catch (err) {
      console.error('Copy failed:', err)
    }
    
    // Clean up
    selection.removeAllRanges()
    document.body.removeChild(tempDiv)
    
    // Return plain text as fallback
    return tempDiv.textContent || tempDiv.innerText || ''
  }

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      generateFullContent() // This function now handles the copy operation directly
      setCopyStatus('success')
      setTimeout(() => setCopyStatus('idle'), 2000)
    } catch (err) {
      setCopyStatus('error')
      setTimeout(() => setCopyStatus('idle'), 2000)
    }
  }

  // Generate plain text content for download
  const generatePlainTextContent = () => {
    let fullContent = `# ${query}\n\n`
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content) {
        const { parsedContent } = parseMarkdownContent(content) || { parsedContent: content }
        
        // Clean content to remove references and bibliographies
        const cleanedContent = parsedContent
          .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
          .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
          .replace(/\[\d+\]/g, '')
          .trim()
        
        fullContent += `## ${section.title}\n\n`
        fullContent += cleanedContent + '\n\n'
      }
    })
    
    return fullContent.trim()
  }

  // Download as markdown
  const handleDownload = () => {
    const fullContent = generatePlainTextContent()
    const blob = new Blob([fullContent], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `makalah-${query.toLowerCase().replace(/\s+/g, '-')}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={GraduationCap}
        title="Makalah Agent"
        query={query}
        iconColor="text-violet-500"
        iconBgColor="bg-violet-500/10"
        actions={isCompleted ? [
          ...(Object.keys(generatedContent).length > 0 ? [{
            onClick: () => setShowAllContent(!showAllContent),
            icon: showAllContent ? EyeOff : Eye,
            label: showAllContent ? 'Sembunyikan Semua' : 'Tampilkan Semua',
            hideOnMobile: true,
            className: cn(buttonVariants.secondary, "flex items-center text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2")
          }] : []),
          {
            onClick: handleCopy,
            icon: Copy,
            label: copyStatus === 'success' ? 'Tersalin!' : copyStatus === 'error' ? 'Gagal' : 'Salin',
            hideOnMobile: true,
            className: cn(
              buttonVariants.secondary,
              "flex items-center text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2",
              copyStatus === 'success' && "bg-green-500/10 text-green-400",
              copyStatus === 'error' && "bg-red-500/10 text-red-400"
            )
          },
          {
            onClick: handleDownload,
            icon: Download,
            label: 'Download',
            hideOnMobile: true,
            className: cn(buttonVariants.primary, "flex items-center text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-2")
          }
        ] : []}
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Progress Section */}
        {isLoading && (
          <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-primary">Membuat Makalah Akademik</h2>
              <div className="flex items-center space-x-2 text-text-tertiary">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{timeRemaining}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Progress: {progress.current} dari {progress.total} bagian</span>
                <span className="text-text-primary font-medium">{progressPercentage}%</span>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-2">
                <div 
                  className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Sources Section - Moved to top after progress */}
        {academicSources.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-6">Sumber Referensi Akademik</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {academicSources.slice(0, 15).map((source, index) => (
                <SourceCard key={index} source={source} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Error Section */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-red-400 mb-1">Terjadi Kesalahan</h3>
                <p className="text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Completion Status */}
        {isCompleted && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-emerald-400 mb-1">Makalah Selesai!</h3>
                <p className="text-emerald-300">Makalah akademik dengan {wordStats.totalWords.toLocaleString()} kata telah berhasil dibuat.</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        {wordStats.totalWords > 0 && (
          <div className="bg-bg-secondary rounded-xl p-6 mb-8 border border-border-primary">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Statistik Makalah</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-500">{wordStats.totalWords.toLocaleString()}</div>
                <div className="text-sm text-text-tertiary">Total Kata</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-500">{outline.length}</div>
                <div className="text-sm text-text-tertiary">Bagian</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-500">{academicSources.length}</div>
                <div className="text-sm text-text-tertiary">Sumber</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-500">{allCitations.length}</div>
                <div className="text-sm text-text-tertiary">Sitasi</div>
              </div>
            </div>
          </div>
        )}

        {/* Content Sections */}
        <div className="space-y-6">
          {outline.map((section, index) => {
            const content = generatedContent[section.id]
            const isExpanded = expandedSections.has(section.id)
            const hasContent = content && content.trim().length > 0
            const wordCount = hasContent ? countWords(content) : 0
            
            return (
              <div key={section.id} className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
                <div 
                  className="p-6 cursor-pointer hover:bg-bg-tertiary/50 transition-colors"
                  onClick={() => hasContent && toggleSection(section.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-text-tertiary bg-bg-tertiary px-2 py-1 rounded">
                        #{index + 1}
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
                        {section.description && (
                          <p className="text-sm text-text-tertiary mt-1">{section.description}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {hasContent && (
                        <div className="text-sm text-text-tertiary">
                          {wordCount.toLocaleString()} kata
                        </div>
                      )}
                      
                      {!hasContent && isLoading && progress.current > index && (
                        <Loader className="w-5 h-5 text-emerald-500 animate-spin" />
                      )}
                      
                      {hasContent && (
                        <button className="p-1 text-text-tertiary hover:text-text-primary transition-colors">
                          {isExpanded ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      )}
                      
                      {hasContent && (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  </div>
                </div>
                
                {hasContent && (isExpanded || showAllContent) && (
                  <div className="border-t border-border-secondary p-6">
                    <MarkdownContent content={content} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

MakalahResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  generatedContent: PropTypes.object.isRequired,
  academicSources: PropTypes.array,
  progress: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired
}

export default MakalahResultPage